import { MdAdd, MdRemove, MdCheck, MdShoppingBasket } from 'react-icons/md';
import type { StorefrontProducto } from '@almacenes/core';
import { toTitleCase, formatPrecio, BRAND } from '../utils';

interface Props {
  producto: StorefrontProducto;
  enCarrito: number;
  onAgregar: () => void;
  onCambiarCantidad: (delta: number) => void;
  onEditarGranel: () => void;
}

const BtnAgregar = ({ onClick, label, children }: { onClick: () => void; label: string; children: React.ReactNode }) => (
  <button
    onClick={onClick}
    className="mt-1 w-full text-xs py-1.5 rounded-lg font-medium border transition-all duration-150 flex items-center justify-center gap-1 active:scale-95"
    style={{ borderColor: BRAND.cyan, color: BRAND.cyan, backgroundColor: 'transparent' }}
    onMouseEnter={(e) => { e.currentTarget.style.backgroundColor = BRAND.cyan; e.currentTarget.style.color = '#fff'; }}
    onMouseLeave={(e) => { e.currentTarget.style.backgroundColor = 'transparent'; e.currentTarget.style.color = BRAND.cyan; }}
    aria-label={label}
  >
    {children}
  </button>
);

export const ProductoCard = ({ producto, enCarrito, onAgregar, onCambiarCantidad, onEditarGranel }: Props) => {
  const esGranel = producto.venderPor === 'F';

  return (
    <article
      className="group bg-white rounded-2xl border border-gray-200 overflow-hidden flex flex-col w-full shadow-sm hover:shadow-lg hover:-translate-y-0.5 transition-all duration-200"
      aria-label={toTitleCase(producto.nombre)}
    >
      <div className="w-full aspect-[4/3] overflow-hidden">
        {producto.imagenUrl ? (
          <img
            src={producto.imagenUrl}
            alt={producto.nombre}
            className="w-full h-full object-contain bg-gray-50 group-hover:scale-105 transition-transform duration-500 ease-out"
            loading="lazy"
          />
        ) : (
          <div
            className="w-full h-full flex items-center justify-center group-hover:brightness-95 transition-all duration-300"
            style={{ backgroundColor: '#FFF7ED' }}
            aria-hidden="true"
          >
            <MdShoppingBasket
              className="text-5xl transition-transform duration-300 group-hover:scale-110"
              style={{ color: '#FDBA74' }}
            />
          </div>
        )}
      </div>

      <div className="p-2.5 flex flex-col flex-1 gap-1.5">
        <p className="text-xs font-medium line-clamp-2 leading-tight text-gray-700">
          {toTitleCase(producto.nombre)}
        </p>

        {esGranel && (
          <span
            className="inline-block text-[10px] font-semibold px-2 py-0.5 rounded-full w-fit border"
            style={{ backgroundColor: '#FFF7ED', color: BRAND.orange, borderColor: '#FED7AA' }}
          >
            Por kg
          </span>
        )}

        <p className="text-sm font-bold mt-auto" style={{ color: BRAND.navy }}>
          {formatPrecio(producto.precioVenta)}
          {esGranel && <span className="text-[10px] font-normal text-gray-400"> /kg</span>}
        </p>

        {esGranel ? (
          enCarrito > 0 ? (
            <button
              onClick={onEditarGranel}
              className="mt-1 w-full text-xs py-1.5 rounded-lg font-medium transition-all duration-150 flex items-center justify-center gap-1 hover:opacity-90 active:scale-95"
              style={{ backgroundColor: BRAND.cyan, color: '#fff' }}
              aria-label={`Editar cantidad de ${producto.nombre}: ${enCarrito} kg en carrito`}
            >
              <MdCheck className="text-xs" aria-hidden="true" />
              {enCarrito} kg — Editar
            </button>
          ) : (
            <BtnAgregar onClick={onAgregar} label={`Agregar ${producto.nombre} al carrito`}>
              <MdAdd aria-hidden="true" /> Agregar kg
            </BtnAgregar>
          )
        ) : enCarrito === 0 ? (
          <BtnAgregar onClick={onAgregar} label={`Agregar ${producto.nombre} al carrito`}>
            <MdAdd aria-hidden="true" /> Agregar
          </BtnAgregar>
        ) : (
          <div
            className="mt-1 flex items-center justify-between rounded-lg px-2 py-1"
            style={{ backgroundColor: '#EAF6FD' }}
            role="group"
            aria-label={`Cantidad de ${producto.nombre}`}
          >
            <button
              onClick={() => onCambiarCantidad(-1)}
              aria-label={`Quitar una unidad de ${producto.nombre}`}
              className="w-6 h-6 rounded-full flex items-center justify-center hover:opacity-80 active:scale-90 transition-all"
              style={{ backgroundColor: BRAND.cyan, color: '#fff' }}
            >
              <MdRemove className="text-sm" aria-hidden="true" />
            </button>
            <span className="text-xs font-bold" style={{ color: BRAND.navy }}>{enCarrito}</span>
            <button
              onClick={() => onCambiarCantidad(1)}
              disabled={producto.venderPor === 'U' && producto.stock != null && enCarrito >= producto.stock}
              aria-label={`Agregar una unidad más de ${producto.nombre}`}
              className="w-6 h-6 rounded-full flex items-center justify-center transition-all hover:opacity-80 active:scale-90 disabled:opacity-30 disabled:cursor-not-allowed"
              style={{ backgroundColor: BRAND.cyan, color: '#fff' }}
            >
              <MdAdd className="text-sm" aria-hidden="true" />
            </button>
          </div>
        )}
      </div>
    </article>
  );
};
